import { useState } from 'react'

import { CardFan } from '@taro/cards'
import { Button, Stack, Text } from '@taro/design'
import { haptic } from '@taro/tg'

import type { Reading } from '../../api'
import styles from './reading.module.css'

interface Props {
  reading: Reading
  busy: boolean
  error: string | null
  onDraw: (clientSeed: string, picks: number[]) => void
}

/**
 * Тасовка и выбор карт.
 *
 * Здесь человек делает то, что в старой версии было бутафорией: его
 * движение задаёт половину сида, а его выбор — какие именно карты лягут.
 * Кнопка не загорится, пока он не перетасовал: расклад, где сервер выбрал
 * всё сам, — ровно то, от чего мы уходим.
 */
export function Shuffle({ reading, busy, error, onDraw }: Props) {
  const [seed, setSeed] = useState<string | null>(null)
  const [picks, setPicks] = useState<number[]>([])

  const need = reading.spread.cardCount
  const ready = seed !== null && picks.length === need

  return (
    <Stack gap={4} align="center">
      <Stack gap={1} align="center">
        <Text variant="overline" tone="tertiary">
          {reading.spread.title}
        </Text>
        <Text variant="title-m" as="h2" style={{ textAlign: 'center' }}>
          {seed === null ? 'Перетасуй колоду' : `Выбери ${need === 1 ? 'карту' : `${need} карты`}`}
        </Text>
        <Text variant="body-s" tone="tertiary" measure style={{ textAlign: 'center' }}>
          {seed === null
            ? 'Проведи пальцем по вееру. Движение задаёт половину случайности — вторую половину сервер зафиксировал до того, как ты коснулся экрана.'
            : `Выбрано ${picks.length} из ${need}`}
        </Text>
      </Stack>

      <CardFan
        count={78}
        back="/card-back.webp"
        picks={need}
        onPicksChange={setPicks}
        onEntropy={(digest) => {
          if (seed === null) haptic('tap')
          setSeed(digest)
        }}
        disabled={busy}
      />

      {/* Отпечаток виден ДО раздачи и специально: именно его человек потом
          сверит на экране проверки. Показать его после было бы бессмысленно —
          обещание, данное задним числом, ничего не стоит. */}
      <div className={styles.commit}>
        <Text variant="caption" tone="tertiary">
          Отпечаток колоды
        </Text>
        <code className={styles.hash}>{reading.serverSeedHash}</code>
      </div>

      {error ? (
        <Text variant="body-s" tone="danger">
          {error}
        </Text>
      ) : null}

      <Button
        fullWidth
        size="lg"
        loading={busy}
        disabled={!ready}
        onClick={() => seed && onDraw(seed, picks)}
      >
        Разложить
      </Button>
    </Stack>
  )
}
